import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { featureFlagsService } from '../services/featureFlags'
import ComingSoonPage from '../pages/ComingSoonPage'
import { useAuthStore } from '../store/authStore'
import { 
  defaultComingSoonRouteConfig, 
  isRouteAllowed, 
  isAdminOnlyRoute, 
  shouldRedirectToHome,
  type RouteBlockingConfig 
} from '../services/comingSoonRouteConfig'

/**
 * Coming Soon Gate Component
 * 
 * OCP-Compliant global access control wrapper.
 * 
 * When Coming Soon mode is enabled:
 * - Blocks all routes except those in route configuration
 * - Redirects blocked routes to home (/) which shows Coming Soon page
 * - ADMIN users can bypass to access admin routes (configurable)
 * 
 * When Coming Soon mode is disabled:
 * - Allows normal routing
 * - Coming Soon page is not accessible
 * 
 * This component wraps the entire app routing without modifying existing routes.
 * Route blocking behavior is configurable via comingSoonRouteConfig.ts (OCP extension point).
 */
interface ComingSoonGateProps {
  children: React.ReactNode
  /**
   * Optional route configuration override
   * Allows extending blocking behavior without modifying this component (OCP)
   */
  routeConfig?: RouteBlockingConfig
}

const ComingSoonGate: React.FC<ComingSoonGateProps> = ({ children, routeConfig = defaultComingSoonRouteConfig }) => {
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  
  // Check if user is admin
  const isAdmin = isAuthenticated && user?.role === 'ADMIN'
  
  // Use configurable route blocking strategy (OCP extension point)
  const isRouteAllowedForPath = isRouteAllowed(location.pathname, routeConfig)
  const isAdminRouteForPath = isAdminOnlyRoute(location.pathname, routeConfig)
  const shouldRedirect = shouldRedirectToHome(location.pathname, routeConfig)
  
  // Only enforce Coming Soon on production domains (not Railway preview URLs)
  const isProductionDomain = () => {
    const hostname = window.location.hostname
    return hostname === 'evoltbay.com' || 
           hostname === 'www.evoltbay.com' ||
           hostname === 'voltbay.com' ||
           hostname === 'www.voltbay.com'
  }
  
  const shouldEnforceComingSoon = isProductionDomain()

  // Check flag immediately on mount - CRITICAL for first render
  useEffect(() => {
    let isMounted = true
    
    const checkFlag = async () => {
      try {
        // Force fresh fetch from API
        await featureFlagsService.initialize()
        const enabled = featureFlagsService.isComingSoonEnabled()
        
        if (isMounted) {
          setComingSoonEnabled(enabled)
          setIsLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          setComingSoonEnabled(false)
          setIsLoading(false)
        }
      }
    }

    // Check immediately
    checkFlag()

    // Subscribe to flag changes
    const unsubscribe = featureFlagsService.subscribe(() => {
      if (isMounted) {
        const enabled = featureFlagsService.isComingSoonEnabled()
        setComingSoonEnabled(enabled)
      }
    })

    // Listen for flag change events (when admin saves)
    const handleFlagChange = async () => {
      await featureFlagsService.initialize()
      if (isMounted) {
        const enabled = featureFlagsService.isComingSoonEnabled()
        setComingSoonEnabled(enabled)
      }
    }
    
    window.addEventListener('comingSoonFlagChanged', handleFlagChange)

    // Periodic refresh (every 2 seconds for faster updates)
    const interval = setInterval(async () => {
      await featureFlagsService.initialize()
      if (isMounted) {
        const enabled = featureFlagsService.isComingSoonEnabled()
        setComingSoonEnabled(enabled)
      }
    }, 2000)

    return () => {
      isMounted = false
      unsubscribe()
      window.removeEventListener('comingSoonFlagChanged', handleFlagChange)
      clearInterval(interval)
    }
  }, []) // Empty deps - only run on mount

  // No useEffect redirect needed - render logic handles everything
  // This ensures admin bypass works correctly

  // Show loading state while checking flag - CRITICAL: Don't render children until flag is confirmed
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If coming soon is enabled AND we're on production domain, apply route blocking strategy
  // Skip Coming Soon enforcement on Railway preview URLs (for testing/preview purposes)
  if (comingSoonEnabled && shouldEnforceComingSoon) {
    // Step 1: Check if route is explicitly allowed (e.g., /coming-soon)
    // For /coming-soon, show the Coming Soon page itself
    if (isRouteAllowedForPath) {
      if (location.pathname === '/coming-soon') {
        return <ComingSoonPage />
      }
      return <>{children}</>
    }
    
    // Step 2: Check if route requires admin and user is admin (OCP extension point)
    if (isAdminRouteForPath && isAdmin) {
      return <>{children}</>
    }
    
    // Step 3: Block all other routes - redirect to home (/) which shows Coming Soon page
    // This applies to /login, /register, and any other route not in allowedRoutes
    if (shouldRedirect) {
      navigate('/', { replace: true })
      return <ComingSoonPage />
    }
    
    // Step 4: For home route (/), show coming soon page directly
    return <ComingSoonPage />
  }

  // Coming soon is disabled - allow normal routing
  // Block access to /coming-soon when disabled
  if (location.pathname === '/coming-soon') {
    navigate('/', { replace: true })
    return null
  }

  // Normal app behavior
  return <>{children}</>
}

export default ComingSoonGate
